// Entry point for the Forecast Inputs feature.
// Re-exports the two public symbols consumed by forecast.tsx:
//   ForecastInputs    — coverage section + drawer orchestration
//   MonthlyAvailabilityValue — dash/amount renderer for Monthly Outlook table

import { useState, useCallback, useMemo } from "react";
import { Plus, AlertTriangle, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListForecastInputs,
  useRefreshForecastInputs,
  getListForecastInputsQueryKey,
  getGetForecastOutlookQueryKey,
} from "@workspace/api-client-react";
import type {
  ForecastInputRecord,
  ForecastInputSummary,
  ForecastOutlook,
} from "@workspace/api-client-react";
import { formatMoney } from "@/components/tekledger-ui";
import { AddDrawer } from "./add-drawer";
import { ManageDrawer } from "./manage-drawer";
import type { ForecastContext, InputType, DrawerView, ManageTab } from "./types";
import { formatAmount } from "./helpers";

// ── CoverageCard ──────────────────────────────────────────────────────────────

interface CoverageCardProps {
  label: string;
  inputType: InputType;
  summary: ForecastInputSummary | undefined;
  onAdd: () => void;
  onManage: () => void;
  isAuthoritative: boolean;
}

function CoverageCard({ label, inputType, summary, onAdd, onManage, isAuthoritative }: CoverageCardProps) {
  const avail = summary?.availability;
  const hasData = avail === "amount" || avail === "no_records" || avail === "verified_zero";

  return (
    <div className="fi-coverage-card" data-testid={`fi-coverage-${inputType}`}>
      <div className="fi-coverage-label">{label}</div>
      {!hasData ? (
        <div className="fi-coverage-empty">No source data</div>
      ) : (
        <div className="fi-coverage-summary">
          {avail === "no_records" ? (
            <span>No records</span>
          ) : avail === "verified_zero" ? (
            <span>$0 verified</span>
          ) : (
            <>
              <b>{summary!.activeCount} {inputType === "accrual" ? "open" : "active"}</b>
              <span>
                {formatAmount(summary!.activeAmount)}{" "}
                {inputType === "accrual" ? "incurred" : "future"}
              </span>
            </>
          )}
        </div>
      )}
      {!isAuthoritative && (
        hasData ? (
          <button
            className="tl-btn fi-coverage-btn"
            onClick={onManage}
            aria-label={`Manage ${label}`}
            data-testid={`fi-manage-${inputType}`}
          >
            Manage
          </button>
        ) : (
          <button
            className="tl-btn fi-coverage-btn"
            onClick={onAdd}
            aria-label={`Add ${label}`}
            data-testid={`fi-add-${inputType}`}
          >
            <Plus size={11} aria-hidden="true" /> Add
          </button>
        )
      )}
    </div>
  );
}

// ── ForecastInputs ────────────────────────────────────────────────────────────

export interface ForecastInputsProps {
  companyId: number;
  fiscalYear: number;
  throughPeriod: number;
  departmentCode: string;
  runId: string | null;
  isAuthoritative: boolean;
  outlook: ForecastOutlook | null;
  onOutlookRefreshed: (updated: ForecastOutlook) => void;
}

export function ForecastInputs({
  companyId, fiscalYear, throughPeriod, departmentCode,
  runId, isAuthoritative, outlook, onOutlookRefreshed,
}: ForecastInputsProps) {
  const queryClient = useQueryClient();
  const listParams = useMemo(
    () => ({ companyId, fiscalYear, departmentCode, activeOnly: false }),
    [companyId, fiscalYear, departmentCode],
  );
  const listQueryKey = useMemo(() => getListForecastInputsQueryKey(listParams), [listParams]);

  const { data: inputList, isLoading } = useListForecastInputs(listParams, {
    query: { queryKey: listQueryKey, staleTime: 15_000 },
  });

  const [drawerView, setDrawerView] = useState<DrawerView | null>(null);
  const [manageTab, setManageTab] = useState<ManageTab>("accrual");
  const [addType, setAddType] = useState<InputType | null>(null);

  const ctx: ForecastContext = {
    companyId, fiscalYear, throughPeriod, departmentCode,
    runId, isAuthoritative, outlook,
  };

  const refreshMutation = useRefreshForecastInputs();

  const handleSaved = useCallback((_record: ForecastInputRecord, withRefresh: boolean) => {
    queryClient.invalidateQueries({ queryKey: listQueryKey });
    if (withRefresh && runId && !isAuthoritative) {
      refreshMutation.mutate({ runId, data: { actor: "ER" } }, {
        onSuccess: (updated) => {
          queryClient.setQueryData(getGetForecastOutlookQueryKey(runId), updated);
          onOutlookRefreshed(updated);
        },
      });
    }
    setDrawerView(null);
    setAddType(null);
  }, [queryClient, listQueryKey, runId, isAuthoritative, refreshMutation, onOutlookRefreshed]);

  const openAdd = useCallback((type: InputType) => {
    setAddType(type);
    setDrawerView("add");
  }, []);

  const openManage = useCallback((type: InputType) => {
    setManageTab(type);
    setDrawerView("manage");
  }, []);

  const getSummary = (type: InputType): ForecastInputSummary | undefined =>
    inputList?.summaries.find(s => s.inputType === type);

  const handleStaleRefresh = useCallback(() => {
    if (!runId || isAuthoritative) return;
    refreshMutation.mutate({ runId, data: { actor: "ER" } }, {
      onSuccess: (updated) => {
        queryClient.setQueryData(getGetForecastOutlookQueryKey(runId), updated);
        onOutlookRefreshed(updated);
      },
    });
  }, [runId, isAuthoritative, refreshMutation, queryClient, onOutlookRefreshed]);

  return (
    <>
      <div className="card fi-coverage-section" data-testid="fi-coverage-section">
        <div className="card-head" style={{ marginBottom: 12 }}>
          <div>
            <div className="eyebrow">Financial Control</div>
            <h3>FORECAST INPUTS</h3>
          </div>
          {!isAuthoritative && (
            <button
              className="tl-btn"
              onClick={() => { setDrawerView("manage"); setManageTab("accrual"); }}
              aria-label="Open manage forecast inputs drawer"
              data-testid="fi-manage-all"
            >
              Manage Forecast Inputs
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="fi-coverage-loading" data-testid="fi-coverage-loading" aria-live="polite">
            <Loader2 size={13} className="animate-spin" aria-hidden="true" style={{ color: "#74857e" }} />
            <span>Loading inputs…</span>
          </div>
        ) : (
          <div className="fi-coverage-grid">
            <CoverageCard
              label="Accruals"
              inputType="accrual"
              summary={getSummary("accrual")}
              onAdd={() => openAdd("accrual")}
              onManage={() => openManage("accrual")}
              isAuthoritative={isAuthoritative}
            />
            <CoverageCard
              label="Commitments"
              inputType="commitment"
              summary={getSummary("commitment")}
              onAdd={() => openAdd("commitment")}
              onManage={() => openManage("commitment")}
              isAuthoritative={isAuthoritative}
            />
            <CoverageCard
              label="Planned Spend"
              inputType="planned_spend"
              summary={getSummary("planned_spend")}
              onAdd={() => openAdd("planned_spend")}
              onManage={() => openManage("planned_spend")}
              isAuthoritative={isAuthoritative}
            />
          </div>
        )}

        {!isAuthoritative && (
          <button
            className="tl-btn"
            style={{ marginTop: 12, fontSize: 10, padding: "7px 10px" }}
            onClick={() => { setAddType(null); setDrawerView("add"); }}
            aria-label="Add a new forecast input"
            data-testid="fi-add-input"
          >
            <Plus size={11} aria-hidden="true" /> Add Forecast Input
          </button>
        )}

        {inputList && runId && !isAuthoritative && inputList.workingForecastStale && (
          <div className="fi-stale-inline" role="status" aria-live="polite" data-testid="fi-stale-inline">
            <AlertTriangle size={12} aria-hidden="true" />
            <span>Inputs changed since last Forecast calculation.</span>
            <button
              className="tl-fy-action primary"
              onClick={handleStaleRefresh}
              disabled={refreshMutation.isPending}
              aria-label="Refresh forecast to incorporate latest inputs"
              data-testid="fi-stale-refresh-btn"
            >
              {refreshMutation.isPending ? "Refreshing…" : "Refresh Forecast"}
            </button>
          </div>
        )}
      </div>

      {/* Drawers + backdrop */}
      {drawerView && (
        <>
          <div
            className="fi-drawer-backdrop"
            onClick={() => { setDrawerView(null); setAddType(null); }}
            aria-hidden="true"
            data-testid="fi-drawer-backdrop"
          />
          {drawerView === "add" && (
            <AddDrawer
              ctx={ctx}
              initialType={addType}
              onSaved={handleSaved}
              onClose={() => { setDrawerView(null); setAddType(null); }}
            />
          )}
          {drawerView === "manage" && inputList && (
            <ManageDrawer
              inputs={inputList}
              ctx={ctx}
              initialTab={manageTab}
              onAddNew={(type) => { setAddType(type); setDrawerView("add"); }}
              onClose={() => setDrawerView(null)}
              onRefreshed={onOutlookRefreshed}
            />
          )}
        </>
      )}
    </>
  );
}

// ── MonthlyAvailabilityValue ──────────────────────────────────────────────────

export interface MonthlyAvailabilityValueProps {
  value: { state: string; amount: number | null };
  globalState: string;
}

export function MonthlyAvailabilityValue({ value, globalState }: MonthlyAvailabilityValueProps) {
  if (globalState === "no_source_data") return <span className="mini" style={{ color: "#a0afa8" }}>—</span>;
  if (value.state === "no_source_data") return <span className="mini" style={{ color: "#a0afa8" }}>—</span>;
  if (value.state === "no_records") return <span className="mini" style={{ color: "#74857e" }}>—</span>;
  if (value.state === "verified_zero" || value.amount === 0) return <span>$0</span>;
  return <span>{formatMoney(value.amount!)}</span>;
}
